import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { formatNaira } from '@/lib/utils';
import { CaretLeft, Wallet, ArrowDown, ArrowUp, Clock, Bank } from '@phosphor-icons/react';

export default function WalletPage() {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [escrows, setEscrows] = useState([]);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [wForm, setWForm] = useState({ amount: '', bank_name: '', account_number: '' });

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get('/wallet');
        setWallet(data.wallet);
        setEscrows(data.escrows || []);
        setTxns(data.transactions || []);
      } catch {} finally { setLoading(false); }
    };
    load();
  }, [api]);

  const withdraw = async () => {
    try {
      await api.post('/wallet/withdraw', { amount: parseFloat(wForm.amount), bank_name: wForm.bank_name, account_number: wForm.account_number });
      setShowWithdraw(false);
      window.location.reload();
    } catch (err) { alert(err.response?.data?.detail || 'Withdrawal failed'); }
  };

  if (loading) return <div className="min-h-screen bg-gradient-to-b from-[#E8DDD2] to-[#D6C9B8] flex items-center justify-center"><div className="w-8 h-8 border-3 border-[#D4C5B3] border-t-[#E86A33] rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#E8DDD2] to-[#D6C9B8] pb-24 max-w-[480px] mx-auto" data-testid="wallet-page">
      <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-black/5 px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="w-9 h-9 bg-[#E8DDD2] rounded-full flex items-center justify-center"><CaretLeft size={18} /></button>
        <h1 className="font-heading text-lg font-bold text-[#1C1C1C]">Wallet</h1>
      </header>

      {/* Balance Hero */}
      <div className="bg-gradient-to-br from-[#1A4D2E] to-[#133922] mx-4 mt-4 rounded-2xl p-6 text-center relative overflow-hidden">
        <div className="absolute top-[-60px] right-[-60px] w-[200px] h-[200px] bg-white/5 rounded-full" />
        <p className="text-xs font-bold uppercase tracking-wider text-white/50 mb-2">Available Balance</p>
        <p className="font-heading text-4xl font-black text-white mb-1" data-testid="wallet-balance">{formatNaira(wallet?.balance || 0)}</p>
        <p className="text-xs text-white/50 mb-5">Pending: {formatNaira(wallet?.pending || 0)}</p>
        <button onClick={() => setShowWithdraw(true)} disabled={!wallet?.balance}
          className="bg-white text-[#1A4D2E] font-bold px-8 py-3 rounded-xl relative z-10 disabled:opacity-40 font-body" data-testid="withdraw-button">
          Withdraw Funds
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-px bg-[#E5E3DB] mx-4 mt-4 rounded-xl overflow-hidden">
        <div className="bg-white p-3 text-center"><div className="font-heading text-lg font-bold text-[#1A4D2E]">{formatNaira(wallet?.total_earned || 0)}</div><div className="text-[10px] text-[#5C5C5C]">Total Earned</div></div>
        <div className="bg-white p-3 text-center"><div className="font-heading text-lg font-bold text-[#E86A33]">{escrows.length}</div><div className="text-[10px] text-[#5C5C5C]">In Escrow</div></div>
        <div className="bg-white p-3 text-center"><div className="font-heading text-lg font-bold text-[#F4A261]">{txns.length}</div><div className="text-[10px] text-[#5C5C5C]">Transactions</div></div>
      </div>

      <div className="p-4">
        {/* Pending Escrow */}
        {escrows.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5C5C] mb-2">Pending Escrow</p>
            {escrows.map(e => (
              <div key={e.id} className="bg-white border border-[#F4A261]/30 rounded-xl p-4 mb-2">
                <div className="flex justify-between items-start mb-2">
                  <div><p className="text-sm font-bold text-[#1C1C1C] font-body">{e.job_title}</p><p className="text-xs text-[#5C5C5C]">Awaiting completion</p></div>
                  <span className="font-heading text-lg font-bold text-[#F4A261]">{formatNaira(e.amount)}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Transaction History */}
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5C5C] mb-2">Transaction History</p>
        {txns.length === 0 ? (
          <p className="text-center text-sm text-[#5C5C5C] py-8 font-body">No transactions yet</p>
        ) : (
          <div className="space-y-2">
            {txns.map(t => (
              <div key={t.id} className="flex items-center gap-3 bg-white border border-[#D4C5B3] rounded-xl p-3">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center ${t.type === 'credit' ? 'bg-[#2D6A4F]/10' : 'bg-red-50'}`}>
                  {t.type === 'credit' ? <ArrowDown size={16} className="text-[#2D6A4F]" /> : <ArrowUp size={16} className="text-red-500" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#1C1C1C] font-body truncate">{t.description}</p>
                  <p className="text-[10px] text-[#5C5C5C]">{new Date(t.created_at).toLocaleDateString()}</p>
                </div>
                <span className={`font-heading font-bold ${t.type === 'credit' ? 'text-[#2D6A4F]' : 'text-red-500'}`}>
                  {t.type === 'credit' ? '+' : '-'}{formatNaira(t.amount)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Withdraw Modal */}
      {showWithdraw && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end justify-center" onClick={() => setShowWithdraw(false)}>
          <div className="bg-white rounded-t-2xl p-6 w-full max-w-[480px] animate-in" onClick={e => e.stopPropagation()} data-testid="withdraw-modal">
            <h3 className="font-heading text-xl font-bold text-[#1C1C1C] mb-1">Withdraw Funds</h3>
            <p className="text-sm text-[#5C5C5C] mb-4 font-body">Available: {formatNaira(wallet?.balance || 0)}</p>
            <div className="space-y-3">
              <input data-testid="withdraw-amount" type="number" value={wForm.amount} onChange={e => setWForm(f => ({ ...f, amount: e.target.value }))} placeholder="Amount (₦)" className="w-full bg-[#E8DDD2] border border-[#D4C5B3] rounded-lg px-4 py-3 font-body outline-none" />
              <input data-testid="withdraw-bank" value={wForm.bank_name} onChange={e => setWForm(f => ({ ...f, bank_name: e.target.value }))} placeholder="Bank Name" className="w-full bg-[#E8DDD2] border border-[#D4C5B3] rounded-lg px-4 py-3 font-body outline-none" />
              <input data-testid="withdraw-account" value={wForm.account_number} onChange={e => setWForm(f => ({ ...f, account_number: e.target.value }))} placeholder="Account Number" className="w-full bg-[#E8DDD2] border border-[#D4C5B3] rounded-lg px-4 py-3 font-body outline-none" />
              <div className="flex gap-3">
                <button onClick={() => setShowWithdraw(false)} className="flex-1 bg-[#E8DDD2] text-[#5C5C5C] font-bold py-3 rounded-lg font-body">Cancel</button>
                <button onClick={withdraw} disabled={!wForm.amount} className="flex-1 bg-[#E86A33] text-white font-bold py-3 rounded-lg font-body disabled:opacity-50" data-testid="confirm-withdraw">Withdraw</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
